/**
 * APP.JS v49.1 - SAFE AREA FIX (Native Status Bar)
 *
 * 🔧 v49.1: SAFE AREA FIX - Header überlappt nicht mehr mit Status Bar
 *    - paddingTop für stickyWrapper auf Native (28px)
 *    - contentArea paddingTop angepasst (138px auf Native)
 *
 * 🔧 v49.0: UNIFIED AUTH - Ein Hook für Native und Web
 *    - useAuth() ersetzt useAuth() und useNativeAuth()
 *    - Automatische Plattform-Erkennung
 *    - Kein SDK-Konflikt mehr!
 *
 * 🔧 v47.5: PWA OFFLINE SUPPORT
 * 🔧 v47.4: TOKEN-CONTEXT für zentrales Token-Management
 *
 * @version 49.1.0 - Safe Area Fix
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import './App.css';
import './mobile-fix.css';

// ✅ v49: Universeller Auth-Hook (Native + Web)
import { useAuth } from './hooks/useAuth';

// Context
import { I18nProvider, useTranslation } from './context/I18nContext';
import { HouseholdProvider } from './context/HouseholdContext';
import { ThemeProvider } from './context/ThemeContext';
import { TokenProvider } from './context/TokenContext';
import { OfflineProvider } from './context/OfflineContext';

// ✅ v46: Auth Error Boundary
import AuthErrorBoundary from './Components/Auth/AuthErrorBoundary';

// Pages
import LandingPage from './Pages/LandingPage';
import FeaturesPage from './Pages/FeaturesPage';
import ImpressumPage from './Pages/ImpressumPage';
import DatenschutzPage from './Pages/DatenschutzPage';
import AGBPage from './Pages/AGBPage';
import FAQPage from './Pages/FAQPage';
import InvitePage from './Pages/InvitePage';

// Widgets & Banners
import FeedbackButton from './Components/common/FeedbackButton';
import LanguageSwitcher from './Components/common/LanguageSwitcher';
import OfflineIndicator from './Components/common/OfflineIndicator';
import CookieBanner from './Components/CookieBanner';
import EmailVerificationBanner from './Components/EmailVerificationBanner';
import UpdateBanner from './Components/common/UpdateBanner';

// Components
import Budget from './Components/Budget/Budget';
import Calendar from './Components/calendar/Calendar';
import Notes from './Components/Notes/Notes';
import Tasks from './Components/Tasks/Tasks';
import Shopping from './Components/Shopping/Shopping';
import Recipes from './Components/Recipes/Recipes';
import Household from './Components/Household/Household';
import Assistant from './Components/assistant/Assistant';
import Dashboard from './Components/Dashboard/Dashboard';
import Footer from './Components/Footer';
import SettingsModal from './Components/Settings/SettingsModal';

// ✅ v49: Plattform-Erkennung für Logging
const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform();

console.log('📊 App.js v49.1 loaded - SAFE AREA FIX 📱');
console.log('📱 Platform:', platform, isNative ? '(Native)' : '(Web)');

// ✅ v45: Footer Höhe für Padding
const FOOTER_HEIGHT = 80;

// ===========================
// PLANIVIO LOGO COMPONENT
// ===========================
const PlanivioLogo = React.memo(({ size = 36, variant = 'color' }) => {
  // SVG Logos haben transparenten Hintergrund
  const logoSrc = variant === 'white'
    ? '/planivio_logo_white.svg'
    : '/planivio_logo_primary.svg';

  return (
    <img
      src={logoSrc}
      alt="Planivio"
      style={{
        height: `${size}px`,
        width: 'auto',
        flexShrink: 0,
        objectFit: 'contain'
      }}
    />
  );
});

// ===========================
// MODULE CONFIGURATION
// ===========================
const MODULES = [
  { id: 'dashboard', icon: '📊', labelKey: 'nav.modules.dashboard' },
  { id: 'calendar', icon: '📅', labelKey: 'nav.modules.calendar' },
  { id: 'assistant', icon: '🤖', labelKey: 'nav.modules.assistant' },
  { id: 'notes', icon: '📝', labelKey: 'nav.modules.notes' },
  { id: 'tasks', icon: '✅', labelKey: 'nav.modules.tasks' },
  { id: 'shopping', icon: '🛒', labelKey: 'nav.modules.shopping' },
  { id: 'recipes', icon: '🍳', labelKey: 'nav.modules.recipes' },
  { id: 'household', icon: '👥', labelKey: 'nav.modules.household' },
  { id: 'budget', icon: '💰', labelKey: 'nav.modules.budget' }
];

const SIDEBAR_MODULES = {
  overview: [
    { id: 'dashboard', icon: '📊', labelKey: 'nav.modules.dashboard' }
  ],
  modules: [
    { id: 'shopping', icon: '🛒', labelKey: 'nav.modules.shopping' },
    { id: 'calendar', icon: '📅', labelKey: 'nav.modules.calendar' },
    { id: 'recipes', icon: '🍳', labelKey: 'nav.modules.recipes' },
    { id: 'notes', icon: '📝', labelKey: 'nav.modules.notes' },
    { id: 'tasks', icon: '✅', labelKey: 'nav.modules.tasks' }
  ],
  finances: [
    { id: 'budget', icon: '💰', labelKey: 'nav.modules.budget' }
  ],
  extras: [
    { id: 'assistant', icon: '🤖', labelKey: 'nav.modules.assistant' },
    { id: 'household', icon: '👥', labelKey: 'nav.modules.household' }
  ]
};

// ===========================
// ✅ FAB - Kontextsensitiver Floating Action Button
// ===========================
const MODULE_NEW_ACTIONS = {
  calendar:  { label: 'Neuer Termin',   icon: '📅' },
  shopping:  { label: 'Neue Liste',     icon: '🛒' },
  notes:     { label: 'Neue Notiz',     icon: '📝' },
  tasks:     { label: 'Neue Aufgabe',   icon: '✅' },
  recipes:   { label: 'Neues Rezept',   icon: '🍳' },
  budget:    { label: 'Neuer Eintrag',  icon: '💰' },
  household: { label: 'Einladen',       icon: '👥' }
};

// ===========================
// ✅ v47.3: MODULE RENDERER (Extrahiert für Stabilität)
// ===========================
const ModuleRenderer = React.memo(function ModuleRenderer({ 
  currentModule, 
  visitedModules,
  onNavigate,
  householdName,
  shoppingLists,
  onAddToShoppingList
}) {
  const hiddenStyle = useMemo(() => ({ display: 'none' }), []);
  const visibleStyle = useMemo(() => ({ display: 'block' }), []);
  
  return (
    <>
      <div style={currentModule === 'dashboard' ? visibleStyle : hiddenStyle}>
        <Dashboard 
          active={currentModule === 'dashboard'}
          onNavigate={onNavigate}
          householdName={householdName}
        />
      </div>
      
      {visitedModules.has('calendar') && (
        <div style={currentModule === 'calendar' ? visibleStyle : hiddenStyle}>
          <Calendar active={currentModule === 'calendar'} />
        </div>
      )}
      
      {visitedModules.has('assistant') && (
        <div style={currentModule === 'assistant' ? visibleStyle : hiddenStyle}>
          <Assistant active={currentModule === 'assistant'} />
        </div>
      )}
      
      {visitedModules.has('notes') && (
        <div style={currentModule === 'notes' ? visibleStyle : hiddenStyle}>
          <Notes active={currentModule === 'notes'} />
        </div>
      )}
      
      {visitedModules.has('tasks') && (
        <div style={currentModule === 'tasks' ? visibleStyle : hiddenStyle}>
          <Tasks active={currentModule === 'tasks'} />
        </div>
      )}
      
      {visitedModules.has('shopping') && (
        <div style={currentModule === 'shopping' ? visibleStyle : hiddenStyle}>
          <Shopping active={currentModule === 'shopping'} externalLists={shoppingLists} />
        </div>
      )}
      
      {visitedModules.has('recipes') && (
        <div style={currentModule === 'recipes' ? visibleStyle : hiddenStyle}>
          <Recipes active={currentModule === 'recipes'} onAddToShoppingList={onAddToShoppingList} />
        </div>
      )}
      
      {visitedModules.has('household') && (
        <div style={currentModule === 'household' ? visibleStyle : hiddenStyle}>
          <Household active={currentModule === 'household'} onNavigate={onNavigate} />
        </div>
      )}
      
      {visitedModules.has('budget') && (
        <div style={currentModule === 'budget' ? visibleStyle : hiddenStyle}>
          <Budget active={currentModule === 'budget'} />
        </div>
      )}
    </>
  );
});

// ===========================
// MAIN APP CONTENT
// ===========================
function AppContent() {
  const { t } = useTranslation();
  
  // ✅ v49: Universeller Auth-Hook (Native + Web)
  const {
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
    getAccessToken,
    getIdTokenClaims
  } = useAuth();

  // ✅ v47: Refs für Logto-Funktionen (stabilisiert)
  const getAccessTokenRef = useRef(getAccessToken);
  const getIdTokenClaimsRef = useRef(getIdTokenClaims);
  
  // ✅ v47: Refs aktualisieren wenn sich Funktionen ändern
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
    getIdTokenClaimsRef.current = getIdTokenClaims;
  }, [getAccessToken, getIdTokenClaims]);

  const [currentUser, setCurrentUser] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  // ✅ v46.1: Modul aus localStorage laden (bleibt nach Refresh)
  const [currentModule, setCurrentModule] = useState(() => {
    const saved = localStorage.getItem('planivio_currentModule');
    return saved && MODULES.find(m => m.id === saved) ? saved : 'dashboard';
  });
  
  // ✅ v46.1: Modul in localStorage speichern bei Änderung
  useEffect(() => {
    localStorage.setItem('planivio_currentModule', currentModule);
  }, [currentModule]);
  
  const [currentPage, setCurrentPage] = useState('landing');
  const [accessToken, setAccessToken] = useState(null);
  const [shoppingLists, setShoppingLists] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // ✅ v46.1: Besuchte Module aus localStorage laden
  const [visitedModules, setVisitedModules] = useState(() => {
    try {
      const saved = localStorage.getItem('planivio_visitedModules');
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed);
      }
    } catch (e) {
      console.warn('Could not load visitedModules from localStorage');
    }
    return new Set(['dashboard']);
  });
  
  // ✅ v47.3: Ref für visitedModules - für stabile Prop-Übergabe
  const visitedModulesRef = useRef(visitedModules);
  
  // ✅ v47: Ref aktualisieren wenn sich State ändert
  useEffect(() => {
    visitedModulesRef.current = visitedModules;
  }, [visitedModules]);
  
  // ✅ v46.1: visitedModules in localStorage speichern
  useEffect(() => {
    localStorage.setItem('planivio_visitedModules', JSON.stringify([...visitedModules]));
  }, [visitedModules]);
  
  // ✅ v47: FIX - visitedModules NICHT in Dependencies!
  useEffect(() => {
    setVisitedModules(prev => {
      if (prev.has(currentModule)) {
        return prev; // Keine Änderung = kein Re-Render
      }
      const next = new Set(prev);
      next.add(currentModule);
      return next;
    });
  }, [currentModule]); // ✅ v47: NUR currentModule als Dependency!
  
  useEffect(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('home') === 'true') {
      setShowLandingPage(true);
      return;
    }
    
    const pathToPage = {
      '/features': 'features',
      '/impressum': 'impressum',
      '/datenschutz': 'datenschutz',
      '/agb': 'agb',
      '/faq': 'faq',
      '/invite': 'invite',
      '/kontakt': 'kontakt'
    };
    
    if (pathToPage[path]) {
      setCurrentPage(pathToPage[path]);
    }
  }, []);
  
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchStartTime = useRef(null);
  const hasMoved = useRef(false);
  
  const navigationInfo = useMemo(() => {
    const currentIndex = MODULES.findIndex(m => m.id === currentModule);
    return {
      currentIndex,
      prevModule: currentIndex > 0 ? MODULES[currentIndex - 1] : null,
      nextModule: currentIndex < MODULES.length - 1 ? MODULES[currentIndex + 1] : null,
      currentModuleInfo: MODULES[currentIndex] || MODULES[0]
    };
  }, [currentModule]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (showSwipeHint && isAuthenticated) {
      const timer = setTimeout(() => setShowSwipeHint(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showSwipeHint, isAuthenticated]);

  const userDataLoaded = useRef(false);
  
  // ✅ v47: FIX - Logto-Funktionen NICHT in Dependencies!
  useEffect(() => {
    const loadUserData = async () => {
      if (userDataLoaded.current) return;
      if (!isAuthenticated) return;
      
      userDataLoaded.current = true;
      
      try {
        // ✅ v47: Refs statt direkter Funktionen
        const claims = await getIdTokenClaimsRef.current();
        console.log('✅ Logto Claims loaded');
        
        setCurrentUser({
          id: claims.sub,
          email: claims.email || claims.username,
          name: claims.name || claims.username || 'User',
          avatar: claims.picture,
          emailVerified: claims.email_verified || false
        });
        
        // ✅ v46: Token-Fehler werden jetzt von AuthErrorBoundary gefangen
        try {
          // ✅ v47: Ref statt direkter Funktion
          const token = await getAccessTokenRef.current(process.env.REACT_APP_LOGTO_API_RESOURCE || process.env.REACT_APP_API_URL);
          setAccessToken(token);
        } catch (tokenError) {
          console.warn('⚠️ Access Token nicht verfügbar:', tokenError.message);
          // Bei invalid_grant wird AuthErrorBoundary den Fehler fangen
          if (tokenError.message?.includes('invalid_grant')) {
            throw tokenError; // Re-throw für AuthErrorBoundary
          }
        }
        
        setCurrentOrganization({
          id: claims.organization_id || 'org_demo',
          name: claims.organization_name || t('header.myHousehold'),
          slug: 'demo'
        });
        
      } catch (error) {
        console.error('❌ Error loading user data:', error);
        userDataLoaded.current = false;
        
        // ✅ v46: Bei Auth-Fehlern re-throw für Boundary
        if (error.message?.includes('invalid_grant') || 
            error.message?.includes('Not authenticated')) {
          throw error;
        }
      }
    };

    loadUserData();
  }, [isAuthenticated, t]); // ✅ v47: OHNE getIdTokenClaims, getAccessToken

  // ===========================
  // HANDLERS - ✅ v47.3: Alle mit leeren Dependencies für Stabilität
  // ===========================
  
  const handleModuleChange = useCallback((moduleId) => {
    setCurrentModule(moduleId);
    setMobileMenuOpen(false);
  }, []);

  const handleNavigate = useCallback((moduleId) => {
    setCurrentModule(moduleId);
    setMobileMenuOpen(false);
  }, []);

  // ✅ v49: Vereinfachter Login - useAuth() handhabt Plattform automatisch
  const handleGetStarted = useCallback(async () => {
    try {
      console.log('🔐 SignIn starten (Plattform:', platform, ')');
      const redirectUri = Capacitor.isNativePlatform()
        ? (platform === 'ios' ? 'capacitor://localhost/callback' : 'de.planivio.app://callback')
        : `${process.env.REACT_APP_FRONTEND_URL}/callback`;
      await signIn(redirectUri);
      console.log('✅ SignIn completed');
    } catch (error) {
      console.error('❌ Login Error:', error);
      // Nur bei echten Fehlern alert zeigen (nicht bei User-Abbruch)
      if (!error.message?.includes('cancelled')) {
        alert('Login fehlgeschlagen: ' + error.message);
      }
    }
  }, [signIn]);

  // ✅ v49: Vereinfachter Logout - useAuth() handhabt Plattform automatisch
  const handleLogout = useCallback(async () => {
    try {
      userDataLoaded.current = false;
      setCurrentUser(null);
      setCurrentOrganization(null);
      setCurrentModule('dashboard');
      setCurrentPage('landing');
      setVisitedModules(new Set(['dashboard']));
      setShowSettingsModal(false);

      // localStorage bei Logout leeren
      localStorage.removeItem('planivio_currentModule');
      localStorage.removeItem('planivio_visitedModules');

      console.log('🔐 SignOut starten (Plattform:', platform, ')');
      await signOut();
      console.log('✅ SignOut completed');
    } catch (error) {
      console.error('❌ Sign-out error:', error);
    }
  }, [signOut]);

  const handleResendVerificationEmail = useCallback(async () => {
    console.log('📧 Resend verification email');
  }, []);

  const handlePageNavigate = useCallback((page) => {
    setCurrentPage(page);
    setShowLandingPage(false);
    setShowSettingsModal(false);
    
    const pageToPath = {
      'features': '/features',
      'impressum': '/impressum',
      'datenschutz': '/datenschutz',
      'agb': '/agb',
      'faq': '/faq',
      'invite': '/invite',
      'kontakt': '/kontakt',
      'landing': '/'
    };
    
    if (pageToPath[page]) {
      window.history.pushState({}, '', pageToPath[page]);
    }
    
    window.scrollTo(0, 0);
  }, []);

  const handleAddToShoppingList = useCallback((ingredients, recipeName, servings = null) => {
    const newList = {
      id: `recipe-list-${Date.now()}`,
      name: `🍳 ${recipeName}`,
      items: ingredients.map((ing, index) => ({
        id: `item-${Date.now()}-${index}`,
        name: ing.name,
        quantity: ing.quantity,
        unit: ing.unit,
        checked: false
      })),
      created_at: new Date().toISOString()
    };

    setShoppingLists(prev => [newList, ...prev]);
    setTimeout(() => setCurrentModule('shopping'), 500);
  }, []);

  const handleSettingsNavigate = useCallback((page) => {
    setCurrentModule(page);
    setMobileMenuOpen(false);
    handlePageNavigate(page);
  }, [handlePageNavigate]);

  const openSettingsModal = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  const openMobileMenu = useCallback(() => {
    setMobileMenuOpen(true);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);


  // ===========================
  // ✅ FAB - Create New Event
  // ===========================
  const handleCreateNew = useCallback(() => {
    window.dispatchEvent(new CustomEvent('planivio-create-new', {
      detail: { module: currentModule }
    }));
  }, [currentModule]);

  // ===========================
  // TOUCH HANDLERS
  // ===========================
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchStartTime.current = Date.now();
    hasMoved.current = false;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current !== null) {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX.current);
      const deltaY = Math.abs(touch.clientY - touchStartY.current);
      if (deltaX > 10 || deltaY > 10) {
        hasMoved.current = true;
      }
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;
    const deltaTime = Date.now() - touchStartTime.current;
    
    const minSwipeDistance = 80;
    const maxSwipeTime = 500;
    
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isLongEnough = Math.abs(deltaX) >= minSwipeDistance;
    const isFastEnough = deltaTime <= maxSwipeTime;
    const didMove = hasMoved.current;
    
    if (isHorizontalSwipe && isLongEnough && isFastEnough && didMove) {
      if (deltaX < 0 && navigationInfo.nextModule) {
        setCurrentModule(navigationInfo.nextModule.id);
      } else if (deltaX > 0 && navigationInfo.prevModule) {
        setCurrentModule(navigationInfo.prevModule.id);
      }
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartTime.current = null;
    hasMoved.current = false;
  }, [navigationInfo.nextModule, navigationInfo.prevModule]);

  // ===========================
  // ✅ v50: HOUSEHOLD NAME - Mit echtem Benutzernamen
  // ===========================
  const householdName = useMemo(() => {
    // 1. Organization Name (wenn vorhanden)
    if (currentOrganization?.name) {
      return currentOrganization.name;
    }
    // 2. User Name (Vorname extrahieren)
    if (currentUser?.name) {
      const firstName = currentUser.name.split(' ')[0];
      return `${firstName}s Haushalt`;
    }
    // 3. Fallback
    return t('header.myHousehold');
  }, [currentOrganization?.name, currentUser?.name, t]);

  // ===========================
  // ✅ v45: SIDEBAR MIT EIGENEM SCROLL
  // ===========================
  const renderSidebar = useCallback(() => {
    const sidebarStyles = {
      sidebar: {
        width: '260px',
        minWidth: '260px',
        maxWidth: '260px',
        flexShrink: 0,
        background: 'white',
        borderRight: '1px solid #E2E8F0',
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${FOOTER_HEIGHT}px)`,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 45
      },
      logo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '20px 24px',
        borderBottom: '1px solid #E2E8F0',
        textDecoration: 'none',
        flexShrink: 0
      },
      logoText: { fontSize: '1.5rem', fontWeight: 700, color: '#4A90A4' },
      nav: {
        flex: 1,
        padding: '15px 12px',
        overflowY: 'auto',
        overflowX: 'hidden'
      },
      section: { marginBottom: '20px' },
      sectionTitle: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '0 12px',
        marginBottom: '8px'
      },
      navItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        color: '#4B5563',
        fontWeight: 500,
        fontSize: '0.95rem',
        marginBottom: '4px',
        border: 'none',
        background: 'transparent',
        width: '100%',
        textAlign: 'left'
      },
      navItemActive: {
        background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(74, 144, 164, 0.3)'
      },
      navIcon: { fontSize: '1.3rem', width: '28px', textAlign: 'center' },
      divider: { height: '1px', background: '#E2E8F0', margin: '12px' },
      quickLinks: { padding: '0 12px', marginBottom: '15px' },
      quickLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: '10px',
        fontSize: '0.85rem',
        color: '#6B7280',
        textDecoration: 'none',
        marginBottom: '4px'
      },
      footer: { 
        padding: '15px', 
        borderTop: '1px solid #E2E8F0',
        background: 'white',
        flexShrink: 0
      },
      userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px',
        borderRadius: '12px',
        background: '#F0F9FF',
        cursor: 'pointer',
        transition: 'all 0.2s'
      },
      avatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 600
      },
      userDetails: { flex: 1 },
      userName: { fontWeight: 600, color: '#1F2937', fontSize: '0.9rem' },
      userRole: { fontSize: '0.75rem', color: '#9CA3AF' }
    };

    const renderNavItem = (mod) => (
      <button
        key={mod.id}
        style={{
          ...sidebarStyles.navItem,
          ...(currentModule === mod.id ? sidebarStyles.navItemActive : {})
        }}
        onClick={() => handleModuleChange(mod.id)}
        onMouseEnter={(e) => {
          if (currentModule !== mod.id) {
            e.currentTarget.style.background = '#E8F4F8';
            e.currentTarget.style.color = '#4A90A4';
          }
        }}
        onMouseLeave={(e) => {
          if (currentModule !== mod.id) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#4B5563';
          }
        }}
      >
        <span style={sidebarStyles.navIcon}>{mod.icon}</span>
        <span>{t(mod.labelKey)}</span>
      </button>
    );

    return (
      <aside style={sidebarStyles.sidebar}>
        <a href="/?home=true" style={sidebarStyles.logo}>
          <PlanivioLogo size={36} variant="color" />
        </a>

        <nav style={sidebarStyles.nav}>
          <div style={sidebarStyles.section}>
            <div style={sidebarStyles.sectionTitle}>Übersicht</div>
            {SIDEBAR_MODULES.overview.map(renderNavItem)}
          </div>
          <div style={sidebarStyles.section}>
            <div style={sidebarStyles.sectionTitle}>Module</div>
            {SIDEBAR_MODULES.modules.map(renderNavItem)}
          </div>
          <div style={sidebarStyles.section}>
            <div style={sidebarStyles.sectionTitle}>Finanzen</div>
            {SIDEBAR_MODULES.finances.map(renderNavItem)}
          </div>
          <div style={sidebarStyles.section}>
            <div style={sidebarStyles.sectionTitle}>Extras</div>
            {SIDEBAR_MODULES.extras.map(renderNavItem)}
          </div>

          <div style={sidebarStyles.divider} />

          <div style={sidebarStyles.quickLinks}>
            <a href="/features" style={sidebarStyles.quickLink}>
              <span>✨</span><span>Features</span>
            </a>
            <a href="/faq" style={sidebarStyles.quickLink}>
              <span>❓</span><span>FAQ</span>
            </a>
            <a href="/invite" style={{...sidebarStyles.quickLink, color: '#16A34A'}}>
              <span>🎁</span><span>Freunde einladen</span>
            </a>
          </div>
        </nav>

        <div style={sidebarStyles.footer}>
          <div 
            style={sidebarStyles.userInfo}
            onClick={openSettingsModal}
            title="Einstellungen öffnen"
          >
            <div style={sidebarStyles.avatar}>
              {currentUser?.name?.charAt(0)?.toUpperCase() || '👤'}
            </div>
            <div style={sidebarStyles.userDetails}>
              <div style={sidebarStyles.userName}>{currentUser?.name || 'User'}</div>
              <div style={sidebarStyles.userRole}>Admin</div>
            </div>
            <span style={{ color: '#9CA3AF', fontSize: '1.2rem' }}>⚙️</span>
          </div>
        </div>
      </aside>
    );
  }, [currentModule, currentUser, handleModuleChange, openSettingsModal, t]);

  // ===========================
  // STYLES
  // ===========================
  const styles = useMemo(() => ({
    appLayout: {
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 50%, #B8D4DC 100%)'
    },
    mainContent: {
      flex: 1,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '260px',
      paddingBottom: `${FOOTER_HEIGHT}px`,
      minWidth: 0,
      overflow: 'hidden'
    },
    desktopHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      background: '#FFFFFF',
      borderRadius: '16px',
      border: '2px solid #B8D4DC',
      position: 'fixed',
      top: '12px',
      left: '272px',
      right: '12px',
      zIndex: 50,
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.1)'
    },
    headerTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#1F2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    headerBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 12px',
      background: '#E8F4F8',
      border: '1px solid #B8D4DC',
      borderRadius: '8px',
      color: '#4A90A4',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      textDecoration: 'none'
    },
    headerNewBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      border: 'none',
      borderRadius: '10px',
      color: 'white',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)',
      transition: 'transform 0.15s, box-shadow 0.15s'
    },
    fab: {
      position: 'fixed',
      bottom: `${FOOTER_HEIGHT + 24}px`,
      right: '24px',
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      color: 'white',
      border: 'none',
      fontSize: '1.6rem',
      fontWeight: 300,
      cursor: 'pointer',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 90,
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    desktopContentArea: {
      flex: 1,
      padding: '12px',
      paddingTop: '80px',
      paddingBottom: '20px',
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 50%, #B8D4DC 100%)',
      overflow: 'auto',
      maxWidth: '100%',
      boxSizing: 'border-box'
    },
    appContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 50%, #B8D4DC 100%)',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      paddingBottom: `${FOOTER_HEIGHT}px`
    },
    stickyWrapper: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 50%, #B8D4DC 100%)',
      padding: '0',
      paddingTop: isNative ? '28px' : '0', // ✅ Safe Area für Native Status Bar
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0',
      padding: '10px 12px',
      background: 'transparent',
      borderRadius: '0',
      borderBottom: '1px solid #B8D4DC',
      flexWrap: 'wrap',
      gap: '8px',
      maxWidth: '100%'
    },
    logo: {
      fontSize: '1.5rem',
      fontWeight: 800,
      color: '#4A90A4',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer'
    },
    mobileNavHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 12px',
      background: '#FFFFFF',
      borderRadius: '16px',
      border: '2px solid #B8D4DC',
      marginBottom: '0',
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.1)'
    },
    navArrow: {
      padding: '10px 16px',
      background: '#F3F4F6',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1.2rem',
      cursor: 'pointer',
      color: '#6B7280',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    navArrowDisabled: {
      opacity: 0.3,
      cursor: 'not-allowed'
    },
    currentModuleLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 20px',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      borderRadius: '12px',
      color: 'white',
      fontSize: '1rem',
      fontWeight: 700
    },
    neighborLabel: {
      fontSize: '0.75rem',
      color: '#9CA3AF',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    hamburgerBtn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '40px',
      height: '40px',
      background: '#F3F4F6',
      border: '2px solid #B8D4DC',
      borderRadius: '10px',
      cursor: 'pointer',
      gap: '4px',
      padding: '8px'
    },
    hamburgerLine: {
      width: '18px',
      height: '2px',
      background: '#4A90A4',
      borderRadius: '2px',
      transition: 'all 0.3s'
    },
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 200,
      opacity: mobileMenuOpen ? 1 : 0,
      visibility: mobileMenuOpen ? 'visible' : 'hidden',
      transition: 'opacity 0.3s ease, visibility 0.3s ease',
      backdropFilter: 'blur(2px)'
    },
    drawer: {
      position: 'fixed',
      top: 0,
      right: 0,
      width: '280px',
      height: '100vh',
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.2)',
      transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 300,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      color: 'white'
    },
    drawerCloseBtn: {
      width: '36px',
      height: '36px',
      background: 'rgba(255,255,255,0.2)',
      border: 'none',
      borderRadius: '8px',
      color: 'white',
      fontSize: '1.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    drawerNav: {
      flex: 1,
      padding: '1rem',
      overflowY: 'auto'
    },
    drawerNavItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      width: '100%',
      padding: '14px 16px',
      background: 'transparent',
      border: 'none',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: 500,
      cursor: 'pointer',
      marginBottom: '4px',
      color: '#374151',
      transition: 'all 0.2s',
      textAlign: 'left'
    },
    drawerNavItemActive: {
      background: '#E8F4F8',
      color: '#4A90A4',
      fontWeight: 600
    },
    drawerActions: {
      padding: '1rem',
      borderTop: '1px solid #E5E7EB',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    drawerActionBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      padding: '12px 16px',
      background: '#F3F4F6',
      border: 'none',
      borderRadius: '10px',
      fontSize: '0.9rem',
      color: '#374151',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    drawerLandingLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      padding: '12px 16px',
      background: 'linear-gradient(135deg, rgba(74, 144, 164, 0.1) 0%, rgba(107, 168, 147, 0.1) 100%)',
      border: '1px solid rgba(74, 144, 164, 0.3)',
      borderRadius: '10px',
      fontSize: '0.9rem',
      color: '#4A90A4',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    contentArea: {
      flex: 1,
      padding: '0',
      paddingTop: isNative ? '138px' : '110px'  /* Space for fixed header + navigation + safe area */
    },
    contentWrapper: {
      position: 'relative',
      width: '100%',
      minHeight: 'calc(100vh - 300px)'
    },
    indicator: {
      display: 'flex',
      justifyContent: 'center',
      gap: '6px',
      padding: '12px 0',
      marginTop: '8px'
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none',
      padding: 0
    },
    dotActive: {
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      width: '24px',
      borderRadius: '4px'
    },
    dotInactive: {
      background: '#B8D4DC'
    },
    swipeHint: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '10px 16px',
      color: '#9CA3AF',
      fontSize: '0.8rem',
      background: 'rgba(255,255,255,0.9)',
      borderRadius: '20px',
      margin: '8px auto',
      width: 'fit-content',
      border: '1px solid #B8D4DC'
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 50%, #B8D4DC 100%)',
      color: '#4A90A4'
    },
    spinner: {
      width: '60px',
      height: '60px',
      border: '6px solid #B8D4DC',
      borderTop: '6px solid #4A90A4',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  }), [mobileMenuOpen]);

  const cssAnimations = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  // ===========================
  // LOADING
  // ===========================
  if (isLoading) {
    return (
      <div style={styles.loading}>
        <style>{cssAnimations}</style>
        <div style={styles.spinner} />
        <p style={{ marginTop: '24px', fontSize: '18px', fontWeight: 600 }}>
          {t('app.loading')}
        </p>
      </div>
    );
  }

  // ===========================
  // NOT AUTHENTICATED
  // ===========================
  if (!isAuthenticated) {
    switch (currentPage) {
      case 'features':
        return <><FeaturesPage onGetStarted={handleGetStarted} onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      case 'impressum':
        return <><ImpressumPage onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      case 'datenschutz':
        return <><DatenschutzPage onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      case 'agb':
        return <><AGBPage onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      case 'faq':
        return <><FAQPage onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      case 'invite':
        return <><InvitePage onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
      default:
        return <><LandingPage onGetStarted={handleGetStarted} onNavigate={handlePageNavigate} /><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
    }
  }

  // ===========================
  // AUTHENTICATED - Show Landing
  // ===========================
  if (showLandingPage) {
    const handleBackToApp = () => {
      setShowLandingPage(false);
      setCurrentPage('app');
      window.history.replaceState({}, '', '/');
    };
    
    return (
      <>
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
          color: 'white', padding: '12px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <span style={{ fontWeight: 600 }}>👋 Eingeloggt als {currentUser?.name || 'User'}</span>
          <button onClick={handleBackToApp} style={{
            padding: '8px 16px', background: 'white', color: '#4A90A4',
            border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
          }}>← Zurück zur App</button>
        </div>
        <div style={{ paddingTop: '56px' }}>
          <LandingPage onGetStarted={handleBackToApp} onNavigate={handlePageNavigate} />
        </div>
        <FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} />
      </>
    );
  }

  // ===========================
  // AUTHENTICATED - Info Pages
  // ===========================
  const infoPages = ['features', 'impressum', 'datenschutz', 'agb', 'faq', 'invite', 'kontakt'];
  
  if (isAuthenticated && infoPages.includes(currentPage)) {
    const handleBackToApp = () => {
      setCurrentPage('app');
      window.history.replaceState({}, '', '/');
    };

    const BackBanner = () => (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
        color: 'white', padding: '12px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <span style={{ fontWeight: 600 }}>👋 Eingeloggt als {currentUser?.name || 'User'}</span>
        <button onClick={handleBackToApp} style={{
          padding: '8px 16px', background: 'white', color: '#4A90A4',
          border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer'
        }}>← Zurück zur App</button>
      </div>
    );

    let PageComponent;
    switch (currentPage) {
      case 'features': PageComponent = <FeaturesPage onGetStarted={handleBackToApp} onNavigate={handlePageNavigate} />; break;
      case 'impressum': PageComponent = <ImpressumPage onNavigate={handlePageNavigate} />; break;
      case 'datenschutz': PageComponent = <DatenschutzPage onNavigate={handlePageNavigate} />; break;
      case 'agb': PageComponent = <AGBPage onNavigate={handlePageNavigate} />; break;
      case 'faq': PageComponent = <FAQPage onNavigate={handlePageNavigate} />; break;
      case 'invite': PageComponent = <InvitePage onNavigate={handlePageNavigate} />; break;
      default: PageComponent = null;
    }

    return <><BackBanner /><div style={{ paddingTop: '56px' }}>{PageComponent}</div><FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} /></>;
  }

  // ===========================
  // 🖥️ DESKTOP: Sidebar Layout
  // ===========================
  if (!isMobile) {
    return (
      <>
        <style>{cssAnimations}</style>
        
        <div style={styles.appLayout}>
          {renderSidebar()}

          <div style={styles.mainContent}>
            <EmailVerificationBanner 
              userEmail={currentUser?.email}
              isEmailVerified={currentUser?.emailVerified}
              onResendEmail={handleResendVerificationEmail}
            />

            <header style={styles.desktopHeader}>
              <div style={styles.headerTitle}>
                <span>{navigationInfo.currentModuleInfo.icon}</span>
                <span>{t(navigationInfo.currentModuleInfo.labelKey)}</span>
              </div>
              <div style={styles.headerRight}>
                {MODULE_NEW_ACTIONS[currentModule] && (
                  <button
                    style={styles.headerNewBtn}
                    onClick={handleCreateNew}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(74, 144, 164, 0.4)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(74, 144, 164, 0.3)'; }}
                  >
                    <span>+</span>
                    <span>{MODULE_NEW_ACTIONS[currentModule].label}</span>
                  </button>
                )}
                <a href="/features" style={styles.headerBtn}><span>📖</span><span>Feature Anleitung</span></a>
                <a href="/faq" style={styles.headerBtn}><span>❓</span><span>FAQ</span></a>
                <LanguageSwitcher variant="dropdown" showLabel={true} />
              </div>
            </header>

            <main style={styles.desktopContentArea}>
              <ModuleRenderer
                currentModule={currentModule}
                visitedModules={visitedModules}
                onNavigate={handleNavigate}
                householdName={householdName}
                shoppingLists={shoppingLists}
                onAddToShoppingList={handleAddToShoppingList}
              />
            </main>
          </div>
        </div>

        {MODULE_NEW_ACTIONS[currentModule] && (
          <button
            style={styles.fab}
            onClick={handleCreateNew}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(74, 144, 164, 0.5)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(74, 144, 164, 0.4)'; }}
            title={MODULE_NEW_ACTIONS[currentModule].label}
          >+</button>
        )}

        {!isNative && <Footer isDark={false} />}

        <SettingsModal
          isOpen={showSettingsModal}
          onClose={closeSettingsModal}
          currentUser={currentUser}
          onLogout={handleLogout}
          onNavigate={handleSettingsNavigate}
        />

        <FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} />
      </>
    );
  }

  // ===========================
  // 📱 MOBILE: Original Layout
  // ===========================
  return (
    <>
      <style>{cssAnimations}</style>
      <div style={styles.appContainer}>
        <EmailVerificationBanner 
          userEmail={currentUser?.email}
          isEmailVerified={currentUser?.emailVerified}
          onResendEmail={handleResendVerificationEmail}
        />

        <div style={styles.stickyWrapper}>
          <header style={styles.header}>
            <a href="/?home=true" style={{ ...styles.logo, textDecoration: 'none' }}>
              <PlanivioLogo size={36} variant="color" />
            </a>
            <button style={styles.hamburgerBtn} onClick={openMobileMenu}>
              <span style={styles.hamburgerLine}></span>
              <span style={styles.hamburgerLine}></span>
              <span style={styles.hamburgerLine}></span>
            </button>
          </header>

          {/* ✅ v52: Navigation mit Icon + Name bei Auswahl */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 8px',
            background: 'transparent',
            borderRadius: '0',
            borderBottom: '1px solid #B8D4DC',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none'
          }}>
            {MODULES.map((mod) => {
              const isActive = currentModule === mod.id;
              return (
                <button
                  key={mod.id}
                  onClick={() => handleModuleChange(mod.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isActive ? '6px' : '0',
                    height: '36px',
                    padding: isActive ? '0 12px' : '0 8px',
                    background: isActive ? '#4A90A4' : 'transparent',
                    color: isActive ? 'white' : '#6B7280',
                    border: 'none',
                    borderRadius: '18px',
                    fontSize: '1.1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{mod.icon}</span>
                  {isActive && (
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {t(mod.labelKey)}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              onClick={openMobileMenu}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                padding: 0,
                background: 'transparent',
                color: '#6B7280',
                border: 'none',
                borderRadius: '10px',
                fontSize: '1rem',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              •••
            </button>
          </div>
        </div>

        <div style={styles.overlay} onClick={closeMobileMenu}></div>

        <div style={styles.drawer}>
          <div style={styles.drawerHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <PlanivioLogo size={32} variant="white" />
              <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>planivio</span>
            </div>
            <button style={styles.drawerCloseBtn} onClick={closeMobileMenu}>✕</button>
          </div>

          {currentUser && (
            <div 
              style={{ padding: '1rem', borderBottom: '1px solid #E5E7EB', cursor: 'pointer' }}
              onClick={() => {
                closeMobileMenu();
                openSettingsModal();
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '40px', height: '40px',
                  background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
                  borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700
                }}>{currentUser.name?.charAt(0)?.toUpperCase() || '👤'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1F2937' }}>{currentUser.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>{currentUser.email}</div>
                </div>
                <span style={{ color: '#9CA3AF', fontSize: '1.2rem' }}>⚙️</span>
              </div>
            </div>
          )}

          <nav style={styles.drawerNav}>
            {MODULES.map(mod => (
              <button
                key={mod.id}
                style={{ ...styles.drawerNavItem, ...(currentModule === mod.id ? styles.drawerNavItemActive : {}) }}
                onClick={() => handleModuleChange(mod.id)}
              >
                <span style={{ fontSize: '1.25rem', width: '28px', textAlign: 'center' }}>{mod.icon}</span>
                <span>{t(mod.labelKey)}</span>
              </button>
            ))}
          </nav>

          <div style={styles.drawerActions}>
            <a href="/?home=true" style={styles.drawerLandingLink}><span>🌐</span><span>Zur Startseite</span></a>
            <a href="/features" style={{...styles.drawerActionBtn, textDecoration: 'none'}}><span>📖</span><span>Feature Anleitung</span></a>
            <a href="/faq" style={{...styles.drawerActionBtn, textDecoration: 'none'}}><span>❓</span><span>FAQ</span></a>
            <a href="/invite" style={{
              ...styles.drawerActionBtn, textDecoration: 'none',
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
              border: '1px solid rgba(34, 197, 94, 0.3)', color: '#16A34A'
            }}><span>🎁</span><span>Freunde einladen</span></a>
            <div style={{ padding: '4px 0' }}><LanguageSwitcher variant="inline" showLabel={true} /></div>
            <button style={styles.drawerActionBtn} onClick={handleLogout}><span>🚪</span><span>{t('header.logout')}</span></button>
          </div>
        </div>

        <div style={styles.contentArea}>
          <main>
            <div 
              style={styles.contentWrapper}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* ✅ v47.3: ModuleRenderer als separate Komponente */}
              <ModuleRenderer
                currentModule={currentModule}
                visitedModules={visitedModules}
                onNavigate={handleNavigate}
                householdName={householdName}
                shoppingLists={shoppingLists}
                onAddToShoppingList={handleAddToShoppingList}
              />
            </div>

            <div style={styles.indicator}>
              {MODULES.map((module, index) => (
                <button
                  key={module.id}
                  style={{ ...styles.dot, ...(index === navigationInfo.currentIndex ? styles.dotActive : styles.dotInactive) }}
                  onClick={() => handleModuleChange(module.id)}
                />
              ))}
            </div>

            {showSwipeHint && (
              <div style={styles.swipeHint}><span>👆</span><span>{t('mobile.swipeHint')}</span></div>
            )}
          </main>
        </div>

        {MODULE_NEW_ACTIONS[currentModule] && (
          <button
            style={styles.fab}
            onClick={handleCreateNew}
            title={MODULE_NEW_ACTIONS[currentModule].label}
          >+</button>
        )}

        {!isNative && <Footer isDark={false} />}

        <SettingsModal
          isOpen={showSettingsModal}
          onClose={closeSettingsModal}
          currentUser={currentUser}
          onLogout={handleLogout}
          onNavigate={handleSettingsNavigate}
        />

        <FeedbackButton /><CookieBanner onNavigate={handlePageNavigate} />
      </div>
    </>
  );
}

// ===========================
// ✅ v47.5: MAIN APP MIT OFFLINE PROVIDER
// ===========================
function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        {/* ✅ v47.4: TokenProvider für zentrales Token-Management */}
        <TokenProvider>
          {/* ✅ v47.5: OfflineProvider für PWA Offline-Modus */}
          <OfflineProvider>
            {/* ✅ v47.3: HouseholdProvider am Root */}
            <HouseholdProvider>
              <AuthErrorBoundary>
                {/* ✅ v47.5: Offline-Indikator (erscheint nur wenn offline oder sync pending) */}
                <OfflineIndicator />
                <UpdateBanner />
                <AppContent />
              </AuthErrorBoundary>
            </HouseholdProvider>
          </OfflineProvider>
        </TokenProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
